import posthog from "posthog-js";

import { logger as baseLogger } from "../logger";

const UTF_ADMIN_FLAG = "utf_admin_flag";
const FLAG_LOAD_TIMEOUT = 5000; // 5 seconds timeout

let logger = baseLogger.getSubLogger({ name: "feature-flag" });
export const setLogger = (newLogger: typeof logger) => {
  logger = newLogger;
};

type FeatureFlag = string;

type FeatureFlagState = {
  flagsLoaded: boolean;
  loadingPromise: Promise<void> | null;
  initialized: boolean;
};

export const state: FeatureFlagState = {
  flagsLoaded: false,
  loadingPromise: null,
  initialized: false,
};

/**
 * Check if we're running in an E2E test environment
 */
const isE2EEnvironment = (): boolean => import.meta.env?.REACT_APP_PLAYWRIGHT_RUN === "true";

/**
 * Get feature flags from environment variables (cached).
 * The value is recomputed only when the underlying env string changes –
 * this avoids repetitive string splitting on every call while still
 * behaving correctly inside tests where the env can be mutated and the
 * module re-loaded with `vitest.resetModules()`.
 */
const getEnvironmentFlags = (() => {
  let cachedRaw = "";
  let cachedFlags: string[] = [];

  return (): string[] => {
    const raw = `${import.meta.env?.["REACT_APP_FEATURES"] || ""}`;
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedFlags = raw
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);
    }
    return cachedFlags;
  };
})();

/**
 * Check if PostHog is properly configured
 */
export function isPostHogConfigured(): boolean {
  if (isE2EEnvironment()) return true;
  return !!import.meta.env?.["REACT_APP_PUBLIC_POSTHOG_KEY"];
}

/**
 * Check if feature flags are loaded
 */
export function areFeatureFlagsLoaded(): boolean {
  return state.flagsLoaded;
}

/**
 * Initialize feature flags loading
 */
export function initializeFeatureFlags(): void {
  if (state.initialized) return;

  if (isPostHogConfigured()) {
    if (posthog.getFeatureFlag(UTF_ADMIN_FLAG) !== null) {
      state.flagsLoaded = true;
    } else {
      posthog.onFeatureFlags(() => {
        state.flagsLoaded = true;
      });
    }
  } else {
    state.flagsLoaded = true;
  }
  state.initialized = true;
}

// Initialize on module load
initializeFeatureFlags();

/**
 * Wait for feature flags to load
 */
export function waitForFeatureFlags(): Promise<void> {
  if (state.flagsLoaded) return Promise.resolve();
  if (!isPostHogConfigured()) {
    state.flagsLoaded = true;
    return Promise.resolve();
  }

  if (!state.loadingPromise) {
    state.loadingPromise = Promise.race<void>([
      new Promise<void>((resolve) => {
        posthog.onFeatureFlags(() => {
          state.flagsLoaded = true;
          resolve();
        });
      }),
      new Promise<void>((resolve) =>
        setTimeout(() => {
          logger.warn("Feature flag loading timed out");
          state.flagsLoaded = true;
          resolve();
        }, FLAG_LOAD_TIMEOUT)
      ),
    ]).catch((error) => {
      logger.error("Error loading feature flags:", error);
      state.flagsLoaded = true;
      return;
    });
  }

  return state.loadingPromise || Promise.resolve();
}

/**
 * Check if a feature flag is enabled via PostHog
 */
export function isPosthogFeatureFlagOn(flagname: FeatureFlag): boolean {
  const envEnabled = isFeatureFlagOn(flagname);

  // Environment flag has highest priority
  if (envEnabled) {
    return true;
  }

  // If not configured for PostHog, and environment did not enable, return false
  if (!isPostHogConfigured()) {
    return false;
  }

  if (!state.flagsLoaded && state.initialized) {
    logger.warn(`[isFeatureFlagOn] ${flagname} checked before PostHog flags were loaded`);
  }

  try {
    const phEnabled = posthog.isFeatureEnabled(flagname) === true;
    logger.debug(`[isFeatureFlagOn] ${flagname} PostHog status:`, phEnabled);
    return phEnabled;
  } catch (error) {
    logger.error(`[isFeatureFlagOn] Error checking feature flag ${flagname}:`, error);
    return false;
  }
}

/**
 * Check if a feature flag is enabled via environment variables
 */
export function isFeatureFlagOn(flagname: FeatureFlag): boolean {
  const flags = getEnvironmentFlags();
  const enabled = flags.includes(flagname);
  logger.debug(`[isFeatureFlagOn] ${flagname} ${enabled ? "enabled via env" : "disabled"}`);
  return enabled;
}

// E2E environment override
if (isE2EEnvironment()) {
  logger.info("[E2E OVERRIDE] Applying PostHog isFeatureEnabled mock for E2E run.");

  posthog.isFeatureEnabled = (flagName: string): boolean => {
    const flags = getEnvironmentFlags();

    // Always enable Alternative Cost in E2E runs unless explicitly disabled
    if (flagName === "alternative_cost_enabled") {
      logger.debug("[E2E OVERRIDE] Enabling 'alternative_cost_enabled' by default in tests.");
      return true;
    }

    const isEnabled = flags.includes(flagName);
    logger.debug(`[E2E OVERRIDE] ${flagName} ${isEnabled ? "enabled" : "disabled"}`);
    return isEnabled;
  };
}
